import type { LaneTaskRow, LaneTasksResponse, WorklistCounts, WorklistKey } from '../../api/laneApi';

/**
 * Pure helpers for the Lender task lists (spec 2026-10-08 §4.12, R14, R16,
 * R17). Nothing here fetches or renders; every function has a real,
 * unmocked test in worklists.test.ts.
 */

export type WorklistChoice = WorklistKey | 'all';

/** Chip order is Ryan's ruling R3/R17. */
export const WORKLIST_CHIPS: ReadonlyArray<{ key: WorklistChoice; label: string }> = [
  { key: 'final_response', label: 'Final response' },
  { key: 'provisional', label: 'Provisional' },
  { key: 'needs_reply', label: 'Needs a reply' },
  { key: 'not_sure', label: 'Not sure' },
  { key: 'needs_checking', label: 'Needs checking' },
  { key: 'all', label: 'All' },
];

const CHOICES: ReadonlyArray<string> = WORKLIST_CHIPS.map((chip) => chip.key);

export const isWorklistChoice = (value: unknown): value is WorklistChoice =>
  typeof value === 'string' && CHOICES.indexOf(value) !== -1;

export const WORKLIST_STORAGE_KEY = 'pcp.lenderLane.worklist';

/** `window.localStorage` itself can throw (blocked site data). */
function browserStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** The viewer's last chip. Storage can be missing, blocked, cleared or hold
 * a value from another build: every one of those opens on All. */
export function readStoredWorklist(storage: Pick<Storage, 'getItem'> | null = browserStorage()): WorklistChoice {
  try {
    const value = storage ? storage.getItem(WORKLIST_STORAGE_KEY) : null;
    return isWorklistChoice(value) ? value : 'all';
  } catch {
    return 'all';
  }
}

/** Per-viewer convenience only: a failed write is ignored. */
export function storeWorklist(choice: WorklistChoice, storage: Pick<Storage, 'setItem'> | null = browserStorage()): void {
  try {
    if (storage) storage.setItem(WORKLIST_STORAGE_KEY, choice);
  } catch {
    // Nothing to do: the chip still works for this page view.
  }
}

/** A count only when the server sent a real number -- never a made-up 0. */
export function chipCount(counts: WorklistCounts | undefined, key: WorklistChoice): number | undefined {
  if (!counts) return undefined;
  const value = counts[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export const worklistsSupported = (response: Pick<LaneTasksResponse, 'worklistCounts'>): boolean =>
  typeof response.worklistCounts === 'object' && response.worklistCounts !== null;

const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?Z?)?$/;

/**
 * 'YYYY-MM-DD' -> [year, month, day] when it is a real calendar day, else
 * null. Parsed by hand: `new Date('YYYY-MM-DD')` is UTC midnight and reads as
 * the day before west of Greenwich, and a UK string through `new Date` swaps
 * day and month. A midnight time suffix is tolerated; any other time is not
 * a calendar date and is rejected.
 */
export function parseIsoDay(value: string | null | undefined): [number, number, number] | null {
  if (typeof value !== 'string') return null;
  const match = ISO_DAY_RE.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return null;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day > daysInMonth ? null : [year, month, day];
}

/** DD/MM/YYYY, or null when the value is not a real date (the row then shows no date). */
export function formatDueDate(value: string | null | undefined): string | null {
  const parsed = parseIsoDay(value);
  if (!parsed) return null;
  const [year, month, day] = parsed;
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole calendar days from the viewer's today to the due day; negative when past. */
// Uses the browser's local calendar day (UK staff), so the answer follows the viewer's clock, BST included.
export function daysUntil(value: string | null | undefined, now: Date = new Date()): number | null {
  const parsed = parseIsoDay(value);
  if (!parsed) return null;
  const due = Date.UTC(parsed[0], parsed[1] - 1, parsed[2]);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due - today) / DAY_MS);
}

export type DueTone = 'overdue' | 'soon' | 'later';

export function dueTone(value: string | null | undefined, now: Date = new Date()): DueTone | null {
  const days = daysUntil(value, now);
  if (days === null) return null;
  if (days < 0) return 'overdue';
  if (days <= 7) return 'soon';
  return 'later';
}

const DUE_LABEL: Partial<Record<WorklistKey, string>> = {
  needs_reply: 'Reply by',
  final_response: 'FOS deadline',
  provisional: 'Objection due',
};

/** "Reply by 20/10/2026" -- only on the three lists that carry a date, and only for a real date. */
export function dueText(row: Pick<LaneTaskRow, 'worklist' | 'worklistDueDate'>): string | null {
  const label = row.worklist ? DUE_LABEL[row.worklist] : undefined;
  const date = formatDueDate(row.worklistDueDate);
  return label && date ? `${label} ${date}` : null;
}

export type ClaimLink = { kind: 'ai'; claimId: string } | { kind: 'suggested'; claimRef: string } | null;

/** "Attached by AI" (switch On only) or "Would attach to CG…" (nothing attached yet, e.g. in Watching). */
export function claimLink(row: Pick<LaneTaskRow, 'assignedClaimId' | 'claimLinkSource' | 'aiSuggestedClaimRef'>): ClaimLink {
  if (row.assignedClaimId) return row.claimLinkSource === 'ai' ? { kind: 'ai', claimId: row.assignedClaimId } : null;
  const ref = (row.aiSuggestedClaimRef || '').trim();
  return ref ? { kind: 'suggested', claimRef: ref } : null;
}

export type FinalResponseMarker = 'final' | 'check' | 'suspected' | null;

/**
 * The Final response list's reason codes for "AI thinks this is a final
 * response, please confirm" (caf_worklists.assign_worklist): the AI's own
 * answer could not be verified against the letter (`final_response_uncertain`),
 * or the examples disagree (`unanimous`). A staff "Yes" rewrites the code to
 * `staff_confirmed`; a verified letter carries its own hold or plan code.
 */
const CHECK_REASON_CODES: ReadonlyArray<string> = ['final_response_uncertain', 'unanimous'];

/**
 * Which final-response marker a row shows. A lists-aware backend sends
 * `finalResponseBadge` and it decides whether there is a badge at all. Final
 * review C4 (ruling): the solid 'final' badge is only for a final response
 * that is verified or staff-confirmed; one the AI couldn't prove is 'check'.
 * The server's own `finalResponseCheckNeeded` decides that when sent; until
 * then the list's reason code does. An older backend omits the badge: keep
 * today's two badges exactly, so a frontend that ships first (or a backend
 * rollback) changes nothing staff see.
 */
export function finalResponseMarker(
  row: Pick<LaneTaskRow, 'finalResponseBadge' | 'hasFinalResponse' | 'aiSuspectsFinalResponse'>
    & Partial<Pick<LaneTaskRow, 'finalResponseCheckNeeded' | 'worklist' | 'worklistReasonCode'>>,
): FinalResponseMarker {
  if (typeof row.finalResponseBadge === 'boolean') {
    if (!row.finalResponseBadge) return null;
    if (typeof row.finalResponseCheckNeeded === 'boolean') return row.finalResponseCheckNeeded ? 'check' : 'final';
    const unproven = row.worklist === 'final_response' && CHECK_REASON_CODES.indexOf(row.worklistReasonCode ?? '') !== -1;
    return unproven ? 'check' : 'final';
  }
  if (row.hasFinalResponse === true) return 'final';
  if (row.aiSuspectsFinalResponse === true) return 'suspected';
  return null;
}

/**
 * The lists whose open email offers "Make this an example" (Freshdesk #705
 * item 1a). R16 put the button on Not sure only; Ryan widened it to every list
 * ("add make an example to all categories and not just not sure"). A Record
 * so a new list has to be decided here before the build passes.
 *
 * Needs checking stays in: nothing about its holds makes an email unsafe to
 * teach from. The save route checks only the role, the form makes the curator
 * choose the final-response setting and warns on a clashing example (R5, R7),
 * and several holds (examples disagree, AI disagrees with the examples, the
 * final-response safety net) are fixed by exactly this. A letter that couldn't
 * be read in full is not a Needs checking-only case either: Not sure carries
 * those too (ruling A3(b)) and has always had the button.
 */
const MAKE_EXAMPLE_LISTS: Readonly<Record<WorklistKey, boolean>> = {
  final_response: true,
  provisional: true,
  needs_reply: true,
  not_sure: true,
  needs_checking: true,
};

/** True when the row is on a list that offers "Make this an example". A row
 * on no list (a workflow would handle it) or an unknown list value: false. */
export function canMakeExample(worklist: WorklistKey | null | undefined): boolean {
  return typeof worklist === 'string'
    && Object.prototype.hasOwnProperty.call(MAKE_EXAMPLE_LISTS, worklist)
    && MAKE_EXAMPLE_LISTS[worklist] === true;
}

/** P2's example setting values (Cosmos `final_response_stance`). */
export type StanceChoice = 'final_response' | 'not_final_response';

export interface TeachPreset {
  taskId: string;
  sender: string | null;
  subject: string | null;
  finalResponseStance?: StanceChoice;
}

/** The teach form's preset for one lane row. The stance is pre-filled only
 * when staff have just confirmed or rejected a final response (R10). */
export function teachPreset(row: Pick<LaneTaskRow, 'id' | 'email_from' | 'subject'>, stance: StanceChoice | null): TeachPreset {
  const preset: TeachPreset = { taskId: row.id, sender: row.email_from, subject: row.subject };
  if (stance) preset.finalResponseStance = stance;
  return preset;
}
